import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FiX, FiTrash2, FiShoppingBag, FiPlus, FiMinus } from 'react-icons/fi';
import { useCartStore, useUIStore } from '../../store';
import { formatPrice } from '../../utils/helpers';

const CartDrawer = () => {
  const { cartOpen, setCartOpen } = useUIStore();
  const { items, removeItem, updateQuantity, getTotal, clearCart } = useCartStore();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setCartOpen(false)}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FiShoppingBag className="w-5 h-5 text-[#1a3a6e]" />
                <h2 className="font-display font-bold text-lg text-gray-900">Your Cart</h2>
                <span className="bg-[#1a3a6e] text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  {items.length}
                </span>
              </div>
              <button onClick={() => setCartOpen(false)} className="p-2 hover:bg-gray-100 transition-colors">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiShoppingBag className="w-10 h-10 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">Your cart is empty</p>
                  <button
                    onClick={() => { setCartOpen(false); navigate('/shop'); }}
                    className="btn-primary text-sm"
                  >
                    Start Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item, idx) => (
                    <motion.div
                      key={`${item.id}_${item.selectedVariation}`}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex gap-4 p-3 border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                      <div className="w-20 h-20 bg-gray-50 flex-shrink-0 overflow-hidden">
                        <img
                          src={item.images?.[0] || '/placeholder-chair.jpg'}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={e => e.target.src = 'https://via.placeholder.com/80x80?text=Chair'}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-gray-900 truncate">{item.title}</h4>
                        {item.selectedVariation && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <div
                              className="w-3 h-3 rounded-full border border-gray-300"
                              style={{ background: item.selectedVariation }}
                            />
                            <span className="text-xs text-gray-500">Color</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border border-gray-200">
                            <button
                              onClick={() => updateQuantity(item.id, item.selectedVariation, item.quantity - 1)}
                              className="px-2 py-1 hover:bg-gray-100 transition-colors"
                            >
                              <FiMinus className="w-3 h-3" />
                            </button>
                            <span className="px-3 py-1 text-sm font-semibold border-x border-gray-200">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.selectedVariation, item.quantity + 1)}
                              className="px-2 py-1 hover:bg-gray-100 transition-colors"
                            >
                              <FiPlus className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="font-bold text-[#1a3a6e] text-sm">{formatPrice((item.salePrice||item.price) * item.quantity)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(item.id, item.selectedVariation)}
                        className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-6 py-5 border-t border-gray-100 space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-bold text-gray-900">{formatPrice(getTotal())}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Shipping</span>
                  <span>{getTotal() >= 5000 ? 'FREE' : formatPrice(250)}</span>
                </div>
                <div className="flex justify-between font-bold text-base border-t border-gray-100 pt-3">
                  <span>Total</span>
                  <span className="text-[#1a3a6e]">
                    {formatPrice(getTotal() + (getTotal() >= 5000 ? 0 : 250))}
                  </span>
                </div>
                <button onClick={handleCheckout} className="btn-primary w-full text-center justify-center flex text-sm">
                  Proceed to Checkout
                </button>
                <Link
                  to="/cart"
                  onClick={() => setCartOpen(false)}
                  className="block text-center text-sm text-[#1a3a6e] hover:underline"
                >
                  View Full Cart
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
